import { Inject, Injectable, InjectionToken } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, RouterStateSnapshot } from '@angular/router';

import { Observable, of } from 'rxjs';
import { exhaustMap, skipWhile, take } from 'rxjs/operators';

import { MtnaLogger } from '@mtna/lib-ui';
import { Catalog } from '@mtna/pojo-consumer-ui';

import { RdsDataProductDialogService } from '../../data-product-selection-dialog/data-product-dialog.service';
import { RdsApiService } from './rds-api.service';

export const RDS_ROOT_CATALOG = new InjectionToken<Observable<Catalog>>('RDS Root Catalog');

/**
 * Protect routes that require a data product to be selected.
 * If a data product is not selected, the guard will automatically select it
 * if there is only one option, otherwise it will open the data product selection dialog.
 *
 * Requires the app to provide the `RDS_ROOT_CATALOG` injection token. In your AppModule:
 * @example
 *
 * export function getRootCatalog(store: Store<fromRoot.State>): Observable<Catalog> {
 *   return store.pipe(select(fromRoot.getDataProductRootCatalog));
 * }
 *
 * NgModule({
 *   ...
 *   providers: [
 *     {
 *       provide: RDS_ROOT_CATALOG,
 *       useFactory: getRootCatalog,
 *       deps: [Store]
 *     }
 *   ],
 *   ...
 * })
 * export class AppModule {}
 *
 */
@Injectable({ providedIn: 'root' })
export class RdsDataProductGuard implements CanActivate {
  /** Whether the catalog has been set and actually exists in the root catalog */
  protected get catalogSetAndExists() {
    return this._catalogSetAndExists;
  }
  private _catalogSetAndExists: boolean;

  /** Whether the data product has been set and actually exists in the selected catalog */
  protected get dataProductSetAndExists() {
    return this._dataProductSetAndExists;
  }
  private _dataProductSetAndExists: boolean;

  constructor(
    protected dataProductDialog: RdsDataProductDialogService,
    protected rdsApiService: RdsApiService,
    @Inject(RDS_ROOT_CATALOG) private getRootCatalog: Observable<Catalog>,
    protected logger: MtnaLogger
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.getRootCatalog.pipe(
      skipWhile(catalog => !catalog),
      take(1),
      exhaustMap(rootCatalog => {
        this.determineSetAndExists(rootCatalog);
        if (this.catalogSetAndExists && this.dataProductSetAndExists) {
          // If the catalog and data product are set, proceed
          return of(true);
        } else {
          // Warn user if the catalog is set but can't be found
          // Unset the invalid catalog
          if (this.rdsApiService.isCatalogIdSet() && !this.catalogSetAndExists) {
            this.logger.warn(
              `[RdsDataProductGuard] canActivate - The catalog,
               ${this.rdsApiService.getCatalogId()},is set but can't be found in the root catalog:`,
              rootCatalog
            );
            this.rdsApiService.setCatalogId(null);
          }
          // Warn user if the data product is set but can't be found
          // Unset the invalid data product
          if (this.rdsApiService.isDataProductIdSet() && !this.dataProductSetAndExists) {
            this.logger.warn(
              `[RdsDataProductGuard] canActivate - The data product,
               ${this.rdsApiService.getDataProductId()},is set but can't be found in the catalog ${this.rdsApiService.getCatalogId()}:`,
              rootCatalog
            );
            this.rdsApiService.setDataProductId(null);
          }

          if (!this.catalogSetAndExists) {
            // If the catalog is not set
            // or it is set, but doesn't exist in the root catalog
            return this.selectInitialCatalog(rootCatalog);
          } else {
            // If the catalog is set, use that catalog
            const selectedCatalog = rootCatalog.catalogs.find(c => c.id === this.rdsApiService.getCatalogId());
            return this.selectInitialDataProduct(selectedCatalog, rootCatalog);
          }
        }
      })
    );
  }

  /**
   * Determine if the catalog and data product have been set on the api service and exists in the root catalog
   * @param rootCatalog root catalog for a given RDS environment
   */
  protected determineSetAndExists(rootCatalog: Catalog) {
    // Determine if the catalog is set on the api service and exists in the root catalog
    this._catalogSetAndExists = this.rdsApiService.isCatalogIdSet()
      ? rootCatalog.catalogs.some(c => c.id === this.rdsApiService.getCatalogId())
      : false;

    // Determine if the data product is set on the api service and exists in the selected catalog
    this._dataProductSetAndExists =
      this.catalogSetAndExists && this.rdsApiService.isDataProductIdSet()
        ? rootCatalog.catalogs
            .find(c => c.id === this.rdsApiService.getCatalogId())
            .dataProducts.some(dp => dp.id === this.rdsApiService.getDataProductId())
        : false;
  }

  /**
   * Selects the initial catalog.
   * This method will check if there is only only catalog available and select it,
   * or if there are multiple options it will open the data product selection dialog for the user to choose.
   *
   * @param rootCatalog RDS root catalog
   * @returns Observable whether the selection has changed
   */
  private selectInitialCatalog(rootCatalog: Catalog): Observable<boolean> {
    if (!!rootCatalog && rootCatalog.catalogs && rootCatalog.catalogs.length === 1) {
      // If there is only one catalog, go ahead and select it
      const onlyCatalog = rootCatalog.catalogs[0];
      this.rdsApiService.setCatalogId(onlyCatalog.id);
      return this.selectInitialDataProduct(onlyCatalog, rootCatalog);
    } else if (!!rootCatalog && rootCatalog.catalogs && rootCatalog.catalogs.length > 1) {
      // If there is more than one catalog, open the dialog
      return this.dataProductDialog.openSelectDataSourceDialog(rootCatalog, true);
    }
  }

  /**
   * Selects the initial data product.
   * If there is only one data product in the provided catalog, select it.
   * If there are multiple data products, open the dialog and let the user choose.
   *
   * @param catalog Selected catalog
   * @param rootCatalog RDS root catalog
   * @returns Observable whether the selection has changed
   */
  private selectInitialDataProduct(catalog: Catalog, rootCatalog: Catalog): Observable<boolean> {
    if (catalog.dataProducts && catalog.dataProducts.length === 1) {
      // If there is only one data product, go ahead and select it
      const onlyDataProduct = catalog.dataProducts[0];
      this.rdsApiService.setDataProductId(onlyDataProduct.id);
      return of(true);
    } else {
      // If there is more than one data product, open the dialog
      return this.dataProductDialog.openSelectDataSourceDialog(rootCatalog, true);
    }
  }
}
